export type SiteNavMode = 'flat' | 'subsite'

export interface SiteNavResult {
  mode: SiteNavMode
  /** flat: categories with isPage annotation; subsite: sub-site nodes with annotated category children */
  items: any[]
}

/**
 * Detects whether a site's top-level children are sub-sites or direct categories.
 * A sub-site has children that themselves have children (categories → pages).
 */
export function detectNavMode(siteChildren: any[]): SiteNavMode {
  return siteChildren.some((child) => {
    const filtered = child.children?.filter((c: any) => c.path !== child.path) ?? []
    return filtered.some(
      (gc: any) => (gc.children?.filter((c: any) => c.path !== gc.path)?.length ?? 0) > 0,
    )
  })
    ? 'subsite'
    : 'flat'
}

/**
 * Resolves the first child leaf at/under a given docs path from a site's nav
 * tree. Used by the docs index routes: a category/subsite `index.md` with no
 * body is a nav container, so landing on it should jump to its first real page
 * (one redirect, resolved to a true leaf — no chained navigateTo, which stalls
 * under SPA). Returns null when the path isn't in the tree or has no children,
 * leaving the caller to render whatever (empty) body it has.
 */
export function resolveFirstChildPath(
  nav: SiteNavResult | null | undefined,
  path: string,
): string | null {
  if (!nav) return null

  function firstLeaf(node: any): string | null {
    const children = node?.children?.filter((c: any) => c.path !== node.path) ?? []
    if (!children.length) return node?.path ?? null
    return firstLeaf(children[0])
  }

  function findByPath(nodes: any[]): any | null {
    for (const node of nodes) {
      if (node?.path === path) return node
      const children = node?.children?.filter((c: any) => c.path !== node.path) ?? []
      const hit = children.length ? findByPath(children) : null
      if (hit) return hit
    }
    return null
  }

  const node = findByPath(nav.items)
  if (!node) return null
  const leaf = firstLeaf(node)
  return leaf && leaf !== path ? leaf : null
}
